import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { MainLayout } from '../components/MainLayout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { 
  User, Mail, Check, X, Clock, 
  Save, Loader2, Eye, EyeOff, Shield, Lock, Bell, BellOff, RefreshCw,
  Smartphone, Home, FileText, Users, BarChart3, Zap, CalendarDays, TrendingUp, PieChart, BookOpen
} from 'lucide-react';
import { 
  isPushSupported, 
  getNotificationPermission, 
  requestNotificationPermission,
  subscribeToPush,
  unsubscribeFromPush,
  sendTestNotification,
  forceRefreshSubscription,
  registerServiceWorker
} from '../utils/pushNotifications';

export default function MyProfilePage() {
  const { user, authFetch, refreshUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profile, setProfile] = useState(null);
  const [pendingEmailChange, setPendingEmailChange] = useState(null);
  
  // Form state
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [hasNameChanges, setHasNameChanges] = useState(false);
  
  // Email change state
  const [showEmailChange, setShowEmailChange] = useState(false);
  const [newEmail, setNewEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [emailChangeLoading, setEmailChangeLoading] = useState(false);

  // Password change state
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [passwordChangeLoading, setPasswordChangeLoading] = useState(false);

  // Push notification state — initialise from localStorage to survive pull-to-refresh
  const [pushSupported, setPushSupported] = useState(false);
  const [pushPermission, setPushPermission] = useState('default');
  const [pushSubscribed, setPushSubscribed] = useState(
    () => localStorage.getItem('opsflow_push_enabled') === 'true'
  );
  const [pushLoading, setPushLoading] = useState(false);

  // Mobile nav customisation state
  const ALL_NAV_OPTIONS = [
    { path: '/', label: 'Today', icon: Home },
    { path: '/dashboard', label: 'Dashboard', icon: BarChart3 },
    { path: '/enquiries', label: 'Enquiries', icon: FileText },
    { path: '/contacts', label: 'Contacts', icon: Users },
    { path: '/pipeline', label: 'Pipeline', icon: Users },
    { path: '/sequences', label: 'Sequences', icon: Zap },
    { path: '/calendar', label: 'Calendar', icon: CalendarDays },
    { path: '/analytics', label: 'Analytics', icon: TrendingUp },
    { path: '/reports', label: 'Reports', icon: PieChart },
    { path: '/library', label: 'Library', icon: BookOpen },
    { path: '/approvals', label: 'Approvals', icon: Shield },
  ];
  const DEFAULT_NAV = ['/', '/enquiries', '/pipeline', '/calendar'];
  const [mobileNav, setMobileNav] = useState(DEFAULT_NAV);
  const [mobileNavSaving, setMobileNavSaving] = useState(false);

  // Helper: update subscribed state + persist
  const setPushState = (enabled) => {
    setPushSubscribed(enabled);
    localStorage.setItem('opsflow_push_enabled', String(enabled));
  };

  useEffect(() => {
    loadProfile();
    checkPushStatus();
    loadMobileNav();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const checkPushStatus = () => {
    const supported = isPushSupported();
    setPushSupported(supported);
    setPushPermission(getNotificationPermission());
    
    if (!supported) return;

    // Only auto-downgrade if browser permission was explicitly revoked
    if (Notification.permission === 'denied') {
      setPushState(false);
      return;
    }

    // Ensure SW is registered for future subscribe/unsubscribe calls.
    // Do NOT query getSubscription() here — on mobile pull-to-refresh it
    // can transiently return null and flip the UI off. localStorage
    // (set by explicit enable/disable actions) is the source of truth.
    registerServiceWorker();
  };

  const loadMobileNav = async () => {
    try {
      const res = await authFetch('/me/mobile-nav');
      if (res.ok) {
        const data = await res.json();
        if (data.items?.length) setMobileNav(data.items);
      }
    } catch { /* use defaults */ }
  };

  const toggleNavItem = (path) => {
    setMobileNav(prev => {
      if (prev.includes(path)) {
        if (prev.length <= 1) return prev; // Keep at least 1
        return prev.filter(p => p !== path);
      }
      if (prev.length >= 4) {
        toast.error('Maximum 4 items. Remove one first.');
        return prev;
      }
      return [...prev, path];
    });
  };

  const saveMobileNav = async () => {
    setMobileNavSaving(true);
    try {
      const res = await authFetch('/me/mobile-nav', {
        method: 'PUT',
        body: { items: mobileNav }
      });
      if (res.ok) {
        localStorage.setItem('opsflow_mobile_nav', JSON.stringify(mobileNav));
        // Notify MainLayout to update immediately
        window.dispatchEvent(new CustomEvent('mobile-nav-updated', { detail: mobileNav }));
        toast.success('Mobile navigation updated');
      } else {
        toast.error('Failed to save navigation');
      }
    } catch {
      toast.error('Failed to save navigation');
    } finally {
      setMobileNavSaving(false);
    }
  };

  useEffect(() => {
    if (profile) {
      const nameChanged = 
        firstName !== profile.first_name || 
        lastName !== profile.last_name;
      setHasNameChanges(nameChanged);
    }
  }, [firstName, lastName, profile]);

  const loadProfile = async () => {
    try {
      const res = await authFetch('/me/profile');
      if (res.ok) {
        const data = await res.json();
        setProfile(data.profile);
        setFirstName(data.profile.first_name || '');
        setLastName(data.profile.last_name || '');
        setPendingEmailChange(data.pending_email_change);
      }
    } catch (err) {
      toast.error('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveName = async () => {
    if (!firstName.trim() || !lastName.trim()) {
      toast.error('First and last name are required');
      return;
    }

    setSaving(true);
    try {
      const res = await authFetch('/me/profile', {
        method: 'PUT',
        body: { first_name: firstName.trim(), last_name: lastName.trim() }
      });

      if (res.ok) {
        const data = await res.json();
        setProfile(data.profile);
        setHasNameChanges(false);
        toast.success('Profile updated successfully');
        // Refresh user context to update header
        if (refreshUser) refreshUser();
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to update profile');
      }
    } catch (err) {
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handleRequestEmailChange = async () => {
    if (!newEmail.trim()) {
      toast.error('Please enter a new email address');
      return;
    }
    if (!password) {
      toast.error('Please enter your current password to verify');
      return;
    }
    if (newEmail.toLowerCase() === profile?.email?.toLowerCase()) {
      toast.error('New email must be different from current email');
      return;
    }

    setEmailChangeLoading(true);
    try {
      const res = await authFetch('/me/request-email-change', {
        method: 'POST',
        body: { new_email: newEmail.trim(), password }
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setPendingEmailChange(newEmail.trim());
        setShowEmailChange(false);
        setNewEmail('');
        setPassword('');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to request email change');
      }
    } catch (err) {
      toast.error('Failed to request email change');
    } finally {
      setEmailChangeLoading(false);
    }
  };

  const handleCancelEmailChange = async () => {
    try {
      const res = await authFetch('/me/cancel-email-change', {
        method: 'POST'
      });

      if (res.ok) {
        toast.success('Email change request cancelled');
        setPendingEmailChange(null);
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to cancel email change');
      }
    } catch (err) {
      toast.error('Failed to cancel email change');
    }
  };

  const handleChangePassword = async () => {
    // Validation
    if (!currentPassword) {
      toast.error('Please enter your current password');
      return;
    }
    if (!newPassword) {
      toast.error('Please enter a new password');
      return;
    }
    if (newPassword.length < 8) {
      toast.error('New password must be at least 8 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    if (currentPassword === newPassword) {
      toast.error('New password must be different from current password');
      return;
    }

    setPasswordChangeLoading(true);
    try {
      const res = await authFetch('/me/password', {
        method: 'PUT',
        body: { 
          current_password: currentPassword, 
          new_password: newPassword 
        }
      });

      if (res.ok) {
        toast.success('Password changed successfully');
        setShowPasswordChange(false);
        setCurrentPassword('');
        setNewPassword('');
        setConfirmPassword('');
      } else {
        const error = await res.json();
        toast.error(error.detail || 'Failed to change password');
      }
    } catch (err) {
      toast.error('Failed to change password');
    } finally {
      setPasswordChangeLoading(false);
    }
  };

  // Push notification handlers
  const handleEnablePush = async () => {
    setPushLoading(true);
    try {
      // Re-read permission in case it changed
      const permission = await requestNotificationPermission();
      setPushPermission(permission);
      
      if (permission === 'granted') {
        const token = localStorage.getItem('opsflow_token');
        if (!token) {
          throw new Error('Please log in again to enable notifications');
        }
        
        await subscribeToPush(token);
        setPushState(true);
        toast.success('Push notifications enabled and verified!');
      } else if (permission === 'denied') {
        toast.error('Notification permission denied. Please enable in browser settings.');
      } else {
        toast.info('Notification permission was dismissed. Please try again.');
      }
    } catch (err) {
      console.error('[Profile] Push subscription error:', err);
      toast.error(err.message || 'Failed to enable push notifications');
    } finally {
      setPushLoading(false);
    }
  };

  const handleDisablePush = async () => {
    setPushLoading(true);
    try {
      const token = localStorage.getItem('opsflow_token');
      await unsubscribeFromPush(token);
      setPushState(false);
      toast.success('Push notifications disabled');
    } catch (err) {
      toast.error('Failed to disable push notifications');
    } finally {
      setPushLoading(false);
    }
  };

  const handleTestPush = async () => {
    try {
      const token = localStorage.getItem('opsflow_token');
      const result = await sendTestNotification(token);
      if (result.success) {
        toast.success(result.message);
      } else {
        // If all subscriptions expired, offer to refresh
        if (result.message?.includes('expired')) {
          toast.error('Notifications expired. Click "Refresh" to fix.');
        } else {
          toast.info(result.message);
        }
      }
    } catch (err) {
      toast.error('Failed to send test notification');
    }
  };

  const handleRefreshPush = async () => {
    setPushLoading(true);
    try {
      const token = localStorage.getItem('opsflow_token');
      if (!token) {
        throw new Error('Please log in again');
      }
      
      // Force refresh the subscription
      await forceRefreshSubscription(token);
      setPushState(true);
      toast.success('Notifications refreshed! Try sending a test now.');
    } catch (err) {
      console.error('[Profile] Refresh error:', err);
      toast.error(err.message || 'Failed to refresh notifications');
    } finally {
      setPushLoading(false);
    }
  };

  if (loading) {
    return (
      <MainLayout>
        <div className="flex items-center justify-center min-h-[400px]">
          <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className="max-w-2xl mx-auto p-4 md:p-6 space-y-6" data-testid="my-profile-page">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-slate-900">My Profile</h1>
        <p className="text-slate-500 mt-1">Manage your account settings and preferences</p>
      </div>

      {/* Profile Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5" />
            Personal Information
          </CardTitle>
          <CardDescription>
            Update your name. Changes will be reflected across the app.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="firstName">First Name</Label>
              <Input
                id="firstName"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Enter first name"
                data-testid="first-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="lastName">Last Name</Label>
              <Input
                id="lastName"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Enter last name"
                data-testid="last-name-input"
              />
            </div>
          </div>

          {hasNameChanges && (
            <div className="flex items-center justify-end pt-2">
              <Button 
                onClick={handleSaveName} 
                disabled={saving}
                data-testid="save-name-btn"
              >
                {saving ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-2" />
                )}
                Save Changes
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Email Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Email Address
          </CardTitle>
          <CardDescription>
            Your email is used for login and notifications. Changing it requires verification.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
            <div>
              <p className="font-medium text-slate-900">{profile?.email}</p>
              <p className="text-sm text-slate-500">Current email address</p>
            </div>
            <Check className="w-5 h-5 text-green-600" />
          </div>

          {/* Pending email change notice */}
          {pendingEmailChange && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-amber-600 mt-0.5" />
                <div className="flex-1">
                  <p className="font-medium text-amber-900">Pending Email Change</p>
                  <p className="text-sm text-amber-700 mt-1">
                    A verification email was sent to <strong>{pendingEmailChange}</strong>. 
                    Please check your inbox and click the verification link.
                  </p>
                  <p className="text-xs text-amber-600 mt-2">
                    Your current email will remain active until verification is complete.
                  </p>
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleCancelEmailChange}
                    className="mt-3 text-amber-700 hover:text-amber-900 hover:bg-amber-100"
                    data-testid="cancel-email-change-btn"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Cancel Request
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Change email button/form */}
          {!pendingEmailChange && !showEmailChange && (
            <Button 
              variant="outline" 
              onClick={() => setShowEmailChange(true)}
              data-testid="change-email-btn"
            >
              <Mail className="w-4 h-4 mr-2" />
              Change Email Address
            </Button>
          )}

          {showEmailChange && (
            <div className="p-4 border border-slate-200 rounded-lg space-y-4">
              <div className="flex items-start gap-2 text-sm text-slate-600 bg-blue-50 p-3 rounded-lg">
                <Shield className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <p>
                  For security, you must re-enter your password. A verification link will be sent to your new email address.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="newEmail">New Email Address</Label>
                <Input
                  id="newEmail"
                  type="email"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                  placeholder="Enter new email"
                  data-testid="new-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">Current Password (Required)</Label>
                <div className="relative">
                  <Input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter your current password"
                    data-testid="password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="flex items-center gap-3 pt-2">
                <Button
                  onClick={handleRequestEmailChange}
                  disabled={emailChangeLoading || !newEmail || !password}
                  data-testid="confirm-email-change-btn"
                >
                  {emailChangeLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Mail className="w-4 h-4 mr-2" />
                  )}
                  Send Verification
                </Button>
                <Button 
                  variant="ghost" 
                  onClick={() => {
                    setShowEmailChange(false);
                    setNewEmail('');
                    setPassword('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Password Change Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Lock className="w-5 h-5" />
            Password
          </CardTitle>
          <CardDescription>
            Change your password to keep your account secure.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!showPasswordChange ? (
            <Button 
              variant="outline" 
              onClick={() => setShowPasswordChange(true)}
              data-testid="change-password-btn"
            >
              <Lock className="w-4 h-4 mr-2" />
              Change Password
            </Button>
          ) : (
            <div className="p-4 border border-slate-200 rounded-lg space-y-4">
              <div className="flex items-start gap-2 text-sm text-slate-600 bg-blue-50 p-3 rounded-lg">
                <Shield className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <p>
                  For security, you must verify your current password before setting a new one.
                </p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="currentPassword">Current Password</Label>
                <div className="relative">
                  <Input
                    id="currentPassword"
                    type={showCurrentPassword ? 'text' : 'password'}
                    value={currentPassword}
                    onChange={(e) => setCurrentPassword(e.target.value)}
                    placeholder="Enter your current password"
                    data-testid="current-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showCurrentPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="newPassword">New Password</Label>
                <div className="relative">
                  <Input
                    id="newPassword"
                    type={showNewPassword ? 'text' : 'password'}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 8 characters)"
                    data-testid="new-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
                <p className="text-xs text-slate-500">Must be at least 8 characters</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">Confirm New Password</Label>
                <Input
                  id="confirmPassword"
                  type={showNewPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm new password"
                  data-testid="confirm-password-input"
                />
                {confirmPassword && newPassword !== confirmPassword && (
                  <p className="text-xs text-red-500">Passwords do not match</p>
                )}
              </div>

              <div className="flex items-center gap-3 pt-2">
                <Button
                  onClick={handleChangePassword}
                  disabled={passwordChangeLoading || !currentPassword || !newPassword || !confirmPassword || newPassword !== confirmPassword}
                  data-testid="confirm-password-change-btn"
                >
                  {passwordChangeLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Lock className="w-4 h-4 mr-2" />
                  )}
                  Update Password
                </Button>
                <Button 
                  variant="ghost" 
                  onClick={() => {
                    setShowPasswordChange(false);
                    setCurrentPassword('');
                    setNewPassword('');
                    setConfirmPassword('');
                  }}
                  data-testid="cancel-password-change-btn"
                >
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Push Notifications Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Bell className="w-5 h-5" />
            Push Notifications
          </CardTitle>
          <CardDescription>
            Get notified about approvals, reminders, wins, and important updates.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {!pushSupported ? (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-700">
                Push notifications are not supported in your browser. Try using Chrome, Firefox, or Edge on desktop, or Chrome on Android.
              </p>
            </div>
          ) : pushPermission === 'denied' ? (
            <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">
                Notification permission was denied. To enable notifications, you'll need to allow them in your browser settings.
              </p>
            </div>
          ) : pushSubscribed ? (
            <div className="space-y-4">
              <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-lg">
                <Bell className="w-5 h-5 text-green-600" />
                <div className="flex-1">
                  <p className="font-medium text-green-800">Notifications Enabled</p>
                  <p className="text-sm text-green-600">You'll receive push notifications for important updates.</p>
                </div>
              </div>
              
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleTestPush}
                  data-testid="test-push-btn"
                >
                  <Bell className="w-4 h-4 mr-2" />
                  Send Test
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRefreshPush}
                  disabled={pushLoading}
                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 border-blue-200"
                  data-testid="refresh-push-btn"
                >
                  {pushLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="w-4 h-4 mr-2" />
                  )}
                  Refresh
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDisablePush}
                  disabled={pushLoading}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200"
                  data-testid="disable-push-btn"
                >
                  {pushLoading ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <BellOff className="w-4 h-4 mr-2" />
                  )}
                  Disable
                </Button>
              </div>
              
              <div className="text-xs text-slate-500 space-y-1">
                <p><strong>You'll be notified about:</strong></p>
                <ul className="list-disc list-inside pl-2 space-y-0.5">
                  <li>Approval requests & decisions</li>
                  <li>Quote wins & losses</li>
                  <li>Task reminders</li>
                  <li>Follow-up reminders</li>
                  <li>Calendar events</li>
                </ul>
                <p className="mt-2 text-amber-600">
                  <strong>Not receiving notifications?</strong> Click "Refresh" to fix.
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-slate-600">
                Enable push notifications to stay updated on approvals, reminders, and important events even when the app is closed.
              </p>
              <Button
                onClick={handleEnablePush}
                disabled={pushLoading}
                className="bg-[#1e3a5f] hover:bg-[#162c46]"
                data-testid="enable-push-btn"
              >
                {pushLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Bell className="w-4 h-4 mr-2" />
                )}
                Enable Push Notifications
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Mobile Navigation Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Smartphone className="w-5 h-5" />
            Mobile Navigation
          </CardTitle>
          <CardDescription>
            Choose up to 4 pages for your bottom navigation bar. The rest will be in the "More" menu.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-2">
            {ALL_NAV_OPTIONS.map((opt) => {
              const Icon = opt.icon;
              const selected = mobileNav.includes(opt.path);
              return (
                <button
                  key={opt.path}
                  onClick={() => toggleNavItem(opt.path)}
                  data-testid={`nav-option-${opt.path.replace('/', '') || 'home'}`}
                  className={`flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${
                    selected
                      ? 'border-[#1e3a5f] bg-[#1e3a5f]/5 text-[#1e3a5f]'
                      : 'border-slate-200 text-slate-500 hover:border-slate-300'
                  }`}
                >
                  <Icon className="w-4 h-4 flex-shrink-0" />
                  <span className="text-sm font-medium">{opt.label}</span>
                  {selected && (
                    <Check className="w-4 h-4 ml-auto flex-shrink-0" />
                  )}
                </button>
              );
            })}
          </div>

          <div className="flex items-center justify-between pt-2">
            <p className="text-xs text-slate-500">{mobileNav.length}/4 selected</p>
            <Button
              onClick={saveMobileNav}
              disabled={mobileNavSaving || mobileNav.length === 0}
              size="sm"
              className="bg-[#1e3a5f] hover:bg-[#162c46]"
              data-testid="save-mobile-nav-btn"
            >
              {mobileNavSaving ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Save className="w-4 h-4 mr-2" />
              )}
              Save
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Account Info Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5" />
            Account Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-slate-500">Role</p>
              <p className="font-medium text-slate-900 capitalize">{profile?.role?.replace('_', ' ')}</p>
            </div>
            <div>
              <p className="text-slate-500">Team</p>
              <p className="font-medium text-slate-900">{user?.team_name || 'N/A'}</p>
            </div>
            <div>
              <p className="text-slate-500">Member Since</p>
              <p className="font-medium text-slate-900">
                {profile?.created_at ? new Date(profile.created_at).toLocaleDateString('en-GB') : 'N/A'}
              </p>
            </div>
            <div>
              <p className="text-slate-500">Status</p>
              <p className="font-medium text-green-600">Active</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
    </MainLayout>
  );
}
